import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { chatbotService } from '../services/chatbotService';
import { ArrowLeft, ArrowRight, Save, Upload, Code, Eye } from 'lucide-react';
import { toast } from 'react-hot-toast';
import KnowledgeSourceManager from '../components/KnowledgeSourceManager';
import IntentBuilder from '../components/IntentBuilder';

const BotWizardNew = () => {
  const navigate = useNavigate();
  const { botId } = useParams();
  const isEditMode = !!botId;

  const [currentStep, setCurrentStep] = useState(1);
  const [botData, setBotData] = useState({
    name: '',
    description: '',
    avatar_url: '',
    welcome_message: 'Hello! How can I help you today?',
    fallback_message: "I'm not sure I understand. Could you rephrase that?",
    knowledge_sources: [],
    intents: [],
    model: 'gemini-pro',
    temperature: 0.7,
    max_tokens: 500
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [embedCode, setEmbedCode] = useState('');

  useEffect(() => {
    if (isEditMode) {
      loadBot();
    }
  }, [botId]);

  useEffect(() => {
    if (botId) {
      generateEmbedCode();
    }
  }, [botId]);

  const loadBot = async () => {
    try {
      setLoading(true);
      const data = await chatbotService.getBot(botId);
      setBotData(data);
    } catch (error) {
      console.error('Error loading bot:', error);
      toast.error('Failed to load bot');
    } finally {
      setLoading(false);
    }
  };

  const generateEmbedCode = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.BACKEND_URL;
    const code = `<script>
  (function() {
    var chatbot = document.createElement('script');
    chatbot.src = '${window.location.origin}/chatbot-widget.js';
    chatbot.setAttribute('data-bot-id', '${botId}');
    chatbot.setAttribute('data-api-url', '${backendUrl}');
    document.body.appendChild(chatbot);
  })();
</script>`;
    setEmbedCode(code);
  };

  const handleInputChange = (field, value) => {
    setBotData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (step) => {
    switch(step) {
      case 1:
        return botData.name.trim().length > 0;
      case 2:
        // Knowledge sources are optional - allow proceeding even if none added yet
        return true;
      case 3:
        // Intent step validation: make intents optional for now to allow testing
        return true;
      case 4:
        return true;
      default:
        return true;
    }
  };

  const handleSave = async () => {
    if (!botData.name.trim()) {
      toast.error('Please enter a bot name');
      return;
    }

    try {
      setSaving(true);
      if (isEditMode) {
        const updateData = {
          name: botData.name,
          description: botData.description,
          avatar_url: botData.avatar_url,
          welcome_message: botData.welcome_message,
          fallback_message: botData.fallback_message,
          intents: botData.intents,
          model: botData.model,
          temperature: botData.temperature,
          max_tokens: botData.max_tokens
        };
        await chatbotService.updateBot(botId, updateData);
        toast.success('Bot updated successfully');
        await loadBot();
      } else {
        const newBot = await chatbotService.createBot({
          name: botData.name,
          description: botData.description,
          avatar_url: botData.avatar_url,
          welcome_message: botData.welcome_message,
          fallback_message: botData.fallback_message,
          model: botData.model,
          temperature: botData.temperature,
          max_tokens: botData.max_tokens
        });
        toast.success('Bot created successfully');
        navigate(`/setup/chatbot-manager/edit/${newBot.id}`);
      }
    } catch (error) {
      console.error('Error saving bot:', error);
      toast.error(error.response?.data?.detail || 'Failed to save bot');
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { number: 1, title: 'Basic Details', description: 'Name and messaging' },
    { number: 2, title: 'Knowledge Sources', description: 'Training data' },
    { number: 3, title: 'Intent Builder', description: 'Conversation flows' },
    { number: 4, title: 'Model Settings', description: 'AI configuration' }
  ];

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-indigo-900 mb-2">Basic Details</h3>
        <p className="text-sm text-indigo-700">Configure your bot's identity and messaging</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Bot Name *</label>
        <input
          type="text"
          value={botData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="e.g., Customer Support Bot"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={botData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Brief description of what this bot does..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Avatar URL (Optional)</label>
        <input
          type="url"
          value={botData.avatar_url}
          onChange={(e) => handleInputChange('avatar_url', e.target.value)}
          placeholder="https://example.com/avatar.png"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Greeting Message</label>
        <textarea
          value={botData.welcome_message}
          onChange={(e) => handleInputChange('welcome_message', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Fallback Message</label>
        <textarea
          value={botData.fallback_message}
          onChange={(e) => handleInputChange('fallback_message', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      {/* Preview */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Preview</h4>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-start gap-3">
            {botData.avatar_url ? (
              <img src={botData.avatar_url} alt="Bot" className="h-8 w-8 rounded-full" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-sm font-semibold">
                {botData.name.charAt(0).toUpperCase() || 'B'}
              </div>
            )}
            <div className="flex-1">
              <p className="text-sm text-gray-900">{botData.welcome_message}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-green-900 mb-2">Knowledge Sources</h3>
        <p className="text-sm text-green-700">Add content for your bot to learn from</p>
      </div>

      {botId ? (
        <KnowledgeSourceManager
          botId={botId}
          sources={botData.knowledge_sources || []}
          onUpdate={(sources) => handleInputChange('knowledge_sources', sources)}
        />
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <p className="text-sm text-yellow-800">
            Click "Save & Continue" in Step 1 to add knowledge sources
          </p>
        </div>
      )}

      {!botId && (
        <div className="text-sm text-gray-600 bg-gray-50 rounded p-3 border border-gray-200">
          <strong>After saving the bot, you'll be able to:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Crawl websites for content</li>
            <li>Upload text and PDF files</li>
            <li>Connect CRM data sources</li>
            <li>Add FAQ Q&A pairs</li>
          </ul>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-orange-900 mb-2">Intent Builder</h3>
        <p className="text-sm text-orange-700">Configure conversation routing and responses</p>
      </div>

      <IntentBuilder
        intents={botData.intents}
        onUpdate={(intents) => handleInputChange('intents', intents)}
      />

      {botData.intents.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Add at least one intent with training phrases to proceed to the next step.
          </p>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-purple-900 mb-2">Model Settings</h3>
        <p className="text-sm text-purple-700">Configure AI model and parameters</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Model</label>
        <select
          value={botData.model}
          onChange={(e) => handleInputChange('model', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        >
          <option value="gpt-4">GPT-4 (Most Capable)</option>
          <option value="gpt-4-turbo">GPT-4 Turbo (Fast & Capable)</option>
          <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fast & Efficient)</option>
          <option value="claude-3-opus">Claude 3 Opus</option>
          <option value="claude-3-sonnet">Claude 3 Sonnet</option>
          <option value="gemini-pro">Gemini Pro</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Temperature: {botData.temperature.toFixed(1)}
        </label>
        <input
          type="range"
          min="0"
          max="2"
          step="0.1"
          value={botData.temperature}
          onChange={(e) => handleInputChange('temperature', parseFloat(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>Precise (0)</span>
          <span>Balanced (1)</span>
          <span>Creative (2)</span>
        </div>
        <p className="text-xs text-gray-600 mt-2">
          Lower values make responses more focused and deterministic. Higher values make responses more creative and varied.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Max Tokens</label>
        <input
          type="number"
          value={botData.max_tokens}
          onChange={(e) => handleInputChange('max_tokens', parseInt(e.target.value) || 500)}
          min="50"
          max="4000"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        />
        <p className="text-xs text-gray-600 mt-1">
          Maximum length of the bot's response (50-4000 tokens)
        </p>
      </div>

      {/* Model Info Card */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Current Configuration</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Model:</span>
            <span className="font-medium text-gray-900">{botData.model}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Temperature:</span>
            <span className="font-medium text-gray-900">{botData.temperature.toFixed(1)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Max Tokens:</span>
            <span className="font-medium text-gray-900">{botData.max_tokens}</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading bot...</p>
        </div>
      </div>
    );
  }

  const canProceedToNext = validateStep(currentStep);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/setup/chatbot-manager')}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {isEditMode ? 'Edit Bot' : 'Create New Bot'}
              </h1>
              <p className="text-sm text-gray-600">
                Step {currentStep} of {steps.length}: {steps[currentStep - 1].title}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditMode && currentStep === 4 && (
              <button
                onClick={() => setShowPreview(true)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Eye className="h-4 w-4" />
                Preview
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !botData.name}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Bot'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Step Navigator */}
        <div className="w-64 bg-white border-r border-gray-200 p-4">
          <div className="space-y-2">
            {steps.map((step) => (
              <button
                key={step.number}
                onClick={() => setCurrentStep(step.number)}
                disabled={step.number > 1 && !isEditMode && !botId}
                className={`w-full text-left p-3 rounded-lg transition ${
                  currentStep === step.number
                    ? 'bg-indigo-50 border border-indigo-200'
                    : 'hover:bg-gray-50 border border-transparent'
                } ${step.number > 1 && !isEditMode && !botId ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      currentStep === step.number
                        ? 'bg-indigo-600 text-white'
                        : validateStep(step.number)
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {step.number}
                  </div>
                  <div>
                    <div className={`text-sm font-medium ${currentStep === step.number ? 'text-indigo-900' : 'text-gray-900'}`}>
                      {step.title}
                    </div>
                    <div className="text-xs text-gray-500">{step.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-3xl mx-auto">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </button>
              <button
                onClick={async () => {
                  // Auto-save bot when moving from step 1 to step 2 if not in edit mode
                  if (currentStep === 1 && !isEditMode) {
                    await handleSave();
                  } else {
                    setCurrentStep(Math.min(steps.length, currentStep + 1));
                  }
                }}
                disabled={currentStep === steps.length || !canProceedToNext}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentStep === 1 && !isEditMode ? 'Save & Continue' : 'Next'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview/Share Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Preview & Share</h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-600">
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Embed Code</label>
                <div className="relative">
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-xs font-mono">
                    <code>{embedCode}</code>
                  </pre>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(embedCode);
                      toast.success('Embed code copied!');
                    }}
                    className="absolute top-2 right-2 px-3 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Add this code to your website's HTML to embed the chatbot
                </p>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-900 mb-2">Bot Configuration</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Model:</span>
                    <span className="ml-2 font-medium">{botData.model}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Temperature:</span>
                    <span className="ml-2 font-medium">{botData.temperature.toFixed(1)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Max Tokens:</span>
                    <span className="ml-2 font-medium">{botData.max_tokens}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Intents:</span>
                    <span className="ml-2 font-medium">{botData.intents.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BotWizardNew;
